import { useEffect, useRef } from 'react';
import { equals } from 'ramda';

import buildTrackings, {
    getConfig as getTrackingsBuilderConfig,
} from '@kinopoisk-int/ott-trackings-builder';
import { FilmoscopeAdapter, parseTrackings } from '@kinopoisk-int/ott-player-tracker';
import { flattenTrackingEvents } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTracker/flattenTrackingEvents';
import { NewPlayerTrackerEventsConfig } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTrackerWithPluggableSender/types';
import { TrackingEvents } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTracker/types';
import { PlayerTrackerWithPluggableSender } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTrackerWithPluggableSender';
import { TrackingData } from '@kinopoisk-int/ott-player-tracker/dist/lib/EvgenSender/types';
import { TrackingData as BuilderTrackingData } from '@kinopoisk-int/ott-trackings-builder/lib/types';
import { Environment } from '@kinopoisk-int/kp-environment';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import { clientConfig } from '@/config/client';

import { PlayerTrackerData, StreamTrackingData, TrackingDataPart } from '../../types/tracker';
import { PlayerTrackerContextValue } from '../../ui/player-tracker/context';

import { useTrackerContext } from './use-tracker';
import { useTrackerData } from './use-tracker-data';

type CustomMap = Record<string, string | number | CustomMap[]>;
type Tracking = { trackingEvents: CustomMap };

interface TrackerAdditionalParameters {
    prerollDurationSeconds: number | null;
    prerollTargetContentId: string;
}

interface Props<AdditionalKeys extends keyof BuilderTrackingData> {
    contentId: string;
    streamTrackingData: StreamTrackingData;
    additionalData?: TrackingDataPart<AdditionalKeys>;
    additionalParams?: TrackerAdditionalParameters;
}

const { environment, trackingsHost } = clientConfig;

const isTrackerDataReady = (trackerData: PlayerTrackerData, contentId: string) =>
    trackerData.uuid === contentId;

function unique<T>(arr: T[]): T[] {
    return arr.filter((obj, index) => index === arr.findIndex((otherObj) => equals(obj, otherObj)));
}

function normalizeTrackings(trackings: CustomMap[]) {
    return mergeTrackings(...trackings.map((trackingEvents) => ({ trackingEvents })))
        .trackingEvents;
}

// ToDo OTT-37667 тесты и рефакторинг
function mergeTrackings(...args: Tracking[]): Tracking {
    let result = args[0] ? args[0].trackingEvents : {};

    if (Array.isArray(result)) {
        result = normalizeTrackings(result);
    }

    for (let i = 1; i < args.length; i++) {
        let events = args[i]?.trackingEvents ?? {};

        if (Array.isArray(events)) {
            events = normalizeTrackings(events);
        }

        Object.keys(events).forEach((key) => {
            const event = events[key];

            if (Array.isArray(event)) {
                if (key in result) {
                    const value = result[key];

                    if (Array.isArray(value)) {
                        result[key] = value.concat(event);
                    }
                } else {
                    result[key] = event;
                }
            }
        });
    }

    return {
        trackingEvents: result,
    };
}

function getTrackingsConfig(context: PlayerTrackerData) {
    const [backends, metrikaCounters] = getTrackingsBuilderConfig({
        env:
            environment && [Environment.Production, Environment.Prestable].includes(environment)
                ? 'production'
                : 'testing',
    });

    const trackings = buildTrackings(
        context,
        { ...backends, ottTrackingsBackend: `${trackingsHost}/` },
        metrikaCounters
    );

    return trackings;
}

const initTracker = ({
    trackings,
    trackingData,
    newTracker,
    sender,
}: {
    trackings: {
        trackingEvents: TrackingEvents;
        prerollDuration: number;
    };
    trackingData: Partial<TrackingData>;
    newTracker: PlayerTrackerWithPluggableSender<FilmoscopeAdapter>;
    sender: NonNullable<PlayerTrackerContextValue['sender']>;
}) => {
    const conf: NewPlayerTrackerEventsConfig = {
        heartbeat: unique(trackings.trackingEvents.heartbeat.map((hb) => hb[1])),
        viewed: unique(trackings.trackingEvents.viewed.map((v) => v[1])),
        watchedTime: unique(trackings.trackingEvents.watchedTime.map((wt) => wt[1])),
        prerollDuration: trackings.prerollDuration,
    };

    newTracker.setTrackings(conf);
    if (sender.type === 'composite') {
        sender.evgen.setTrackingData(trackingData);
    }
    sender.htmlRequest.setTrackings(trackings);
};

const PREROLL_CONTENT_TYPE_IDS = [20, 21];

function isPrerollDurationReady(
    prerollDurationSeconds: number | null,
    contentTypeId: number | undefined,
    prerollTargetContentId: string | null
) {
    if (contentTypeId && PREROLL_CONTENT_TYPE_IDS.includes(contentTypeId)) {
        if (prerollDurationSeconds) {
            return prerollTargetContentId;
        }
    }

    return true;
}

export const useInitTracker = <AdditionalKeys extends keyof BuilderTrackingData>({
    contentId,
    additionalData,
    streamTrackingData,
    additionalParams: { prerollDurationSeconds, prerollTargetContentId } = {
        prerollDurationSeconds: null,
        prerollTargetContentId: '',
    },
}: Props<AdditionalKeys>) => {
    const instance = usePlayerInstance();
    const trackerContext = useTrackerContext();

    const isTrackingInitialized = useRef(false);

    const trackerData = useTrackerData({ additionalData, streamTrackingData });

    useEffect(() => {
        if (
            instance &&
            !isTrackingInitialized.current &&
            trackerContext.tracker &&
            trackerContext.sender &&
            isTrackerDataReady(trackerData, contentId) &&
            isPrerollDurationReady(
                prerollDurationSeconds,
                trackerData.contentTypeId,
                prerollTargetContentId
            )
        ) {
            isTrackingInitialized.current = true;
            const tracking = getTrackingsConfig(trackerData);

            const trackings = {
                trackingEvents: flattenTrackingEvents(parseTrackings(tracking).trackingEvents),
                prerollDuration: (prerollDurationSeconds ?? 0) * 1000,
            };

            initTracker({
                trackings,
                trackingData: trackerData,
                newTracker: trackerContext.tracker,
                sender: trackerContext.sender,
            });
        }
    }, [
        contentId,
        instance,
        prerollDurationSeconds,
        prerollTargetContentId,
        trackerContext,
        trackerData,
    ]);

    useEffect(() => {
        if (
            trackerContext.tracker &&
            trackerContext.sender &&
            isPrerollDurationReady(
                prerollDurationSeconds,
                trackerData.contentTypeId,
                prerollTargetContentId
            )
        ) {
            const trackings = {
                trackingEvents: flattenTrackingEvents(
                    parseTrackings(getTrackingsConfig(trackerData)).trackingEvents
                ),
                prerollDuration: (prerollDurationSeconds ?? 0) * 1000,
            };

            initTracker({
                trackings,
                trackingData: trackerData,
                newTracker: trackerContext.tracker,
                sender: trackerContext.sender,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId]);
};
